"use client";

import { STagDto } from "@/__generated__/graphql";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import DOMPurify from "isomorphic-dompurify";
import { memo, useEffect, useRef, useState } from "react";
import ProductBottomSheet from "./ProductBottomSheet";
import ProductDescriptionTags from "./ProductDescriptionTags";

const COLLAPSED_HEIGHT = 240;

const descriptionSx = {
  color: "grey.800",
  fontSize: { xs: "0.95rem", md: "1rem" },
  lineHeight: 2,
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  "& p": {
    margin: "0 0 1em",
  },
  "& p:last-child": {
    marginBottom: 0,
  },
  "& h2, & h3, & h4": {
    margin: "1.25em 0 0.5em",
    lineHeight: 1.6,
    color: "grey.900",
  },
  "& ul, & ol": {
    margin: "0.5em 0 1em",
    paddingInlineStart: "1.25em",
  },
  "& li": {
    marginBottom: "0.35em",
  },
  "& img, & video, & iframe": {
    maxWidth: "100%",
    height: "auto",
  },
  "& table": {
    display: "block",
    maxWidth: "100%",
    overflowX: "auto",
  },
  "& pre": {
    maxWidth: "100%",
    overflowX: "auto",
  },
  "& a": {
    color: "info.main",
  },
} as const;

function ProductMidSectionDescription({ description, tags }: { description?: string | null; tags?: Array<STagDto | null> | null }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const contentRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [canCollapse, setCanCollapse] = useState(false);

  const hasTags = Boolean(tags?.some((tag) => tag?.title));
  const dataText = description
    ? DOMPurify.sanitize(description.replace(/<(\/?)h1(\s|>)/gi, "<$1h2$2"), {
        ADD_TAGS: ["iframe"],
        ADD_ATTR: ["src", "webkitallowfullscreen", "mozallowfullscreen", "allowfullscreen"],
      })
    : "";

  useEffect(() => {
    if (isMobile) {
      setCanCollapse(false);
      return;
    }

    const element = contentRef.current;
    if (!element) {
      setCanCollapse(false);
      return;
    }

    const measure = () => {
      setCanCollapse(element.scrollHeight > COLLAPSED_HEIGHT + 48);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [dataText, isMobile]);

  const descriptionHtml = dataText ? <Box data-description sx={descriptionSx} dangerouslySetInnerHTML={{ __html: dataText }} /> : null;

  const showMobileMore = Boolean(dataText) || hasTags;

  return (
    <Box>
      {dataText ? (
        <Box>
          {/* Mobile: 3-line clamp on the html node. Desktop: max-height collapse. */}
          <Box
            sx={{
              position: "relative",
              maxHeight: { xs: "none", md: canCollapse && !expanded ? COLLAPSED_HEIGHT : "none" },
              overflow: { xs: "visible", md: canCollapse && !expanded ? "hidden" : "visible" },
            }}
          >
            <Box
              ref={contentRef}
              data-description
              sx={{
                ...descriptionSx,
                display: { xs: "-webkit-box", md: "block" },
                WebkitLineClamp: { xs: 3, md: "unset" },
                WebkitBoxOrient: "vertical",
                overflow: { xs: "hidden", md: "visible" },
              }}
              dangerouslySetInnerHTML={{ __html: dataText }}
            />
            {canCollapse && !expanded && (
              <Box
                aria-hidden
                sx={{
                  display: { xs: "none", md: "block" },
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 72,
                  background: (t) => `linear-gradient(to bottom, transparent, ${t.palette.background.paper})`,
                  pointerEvents: "none",
                }}
              />
            )}
          </Box>
          {canCollapse && (
            <Button
              variant="text"
              color="inherit"
              onClick={() => setExpanded((open) => !open)}
              endIcon={expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              sx={{ display: { xs: "none", md: "inline-flex" }, mt: 1, px: 0.5, color: "grey.800" }}
            >
              {expanded ? "بستن توضیحات" : "ادامه توضیحات"}
            </Button>
          )}
        </Box>
      ) : null}

      {showMobileMore ? (
        <Button
          variant="text"
          color="secondary"
          onClick={() => setSheetOpen(true)}
          sx={{ display: { xs: "inline-flex", md: "none" }, mt: dataText ? 0.5 : 0, px: 0.5 }}
        >
          ادامه و بیشتر
        </Button>
      ) : null}

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <ProductDescriptionTags tags={tags} />
      </Box>

      <ProductBottomSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title="توضیحات">
        <Box
          sx={{
            "& [data-description]": {
              fontSize: "0.95rem",
              lineHeight: 1.95,
              color: "grey.800",
            },
            "& [data-description] p": { marginBottom: "0.9em" },
          }}
        >
          {descriptionHtml}
          <ProductDescriptionTags tags={tags} />
        </Box>
      </ProductBottomSheet>
    </Box>
  );
}

export default memo(ProductMidSectionDescription);
